import React, { createRef, useState, useEffect } from "react";
import moment from "moment";
import MaterialTable, { Column, Query, QueryResult } from "material-table";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import SyncIcon from "@material-ui/icons/Sync";
import RefreshIcon from "@material-ui/icons/Refresh";
import FilterList from "@material-ui/icons/FilterList";
import TableFilter from "components/TableFilter";
import {
	searchSystemLog,
	getSystemModules
} from "shared/network/system-log-api";
import LOG_LEVELS from "shared/log-levels";
import styles from "shared/material-table/styles";
import icons from "shared/material-table/icons";
import useInterval from "shared/hook/useInterval";
import { REFRESH_DEFAULT_DELAY } from "config/constants";
import { useTranslation } from "react-i18next";
import Layout from "components/Layout";
import SystemLogType from "shared/types/systemlog.model";
import getMaterialTableLocalization from "shared/material-table/localization";

const useStyles = makeStyles(
	theme => ({
		progress: {
			position: "absolute"
		},
		textTitle: {
			fontWeight: "bold"
		},
		text: {
			whiteSpace: "normal",
			paddingLeft: theme.spacing(2)
		}
	}),
	{ name: "SystemLog" }
);

const options = {
	...styles,
	actionsColumnIndex: -1,
	doubleHorizontalScroll: true,
	pageSizeOptions: [20, 50, 100],
	search: false
};

const SystemLog = () => {
	const { t } = useTranslation();
	const classes = useStyles();
	const tableRef = createRef<any>();
	const [filtering, setFiltering] = useState(false);
	const [isRunning, setIsRunning] = useState(false);
	const [actualPageSize, setActualPageSize] = useState(20);
	const [delay] = useState(REFRESH_DEFAULT_DELAY);

	const columns: Array<Column<SystemLogType>> = [
		{
			title: t("pages.systemlog.timestamp"),
			field: "timestamp",
			type: "date"
		},
		{
			title: t("pages.systemlog.modulName"),
			field: "moduleName",
			filterPlaceholder: t("pages.systemlog.modulName")
		},
		{
			title: t("pages.systemlog.containerId"),
			field: "containerId",
			filterPlaceholder: t("pages.systemlog.containerId")
		},
		{
			title: t("pages.systemlog.level"),
			field: "level",
			lookup: LOG_LEVELS,
			filterPlaceholder: t("pages.systemlog.level")
		}
	];

	options.pageSize = actualPageSize;

	useEffect(() => {
		async function getData() {
			try {
				const { data } = await getSystemModules();
				const modules: any = {};
				data.sort().forEach((item: any) => (modules[item] = item));
				columns[1].lookup = modules;
			} catch (e) {}
		}
		if (!columns[1].lookup) {
			getData();
		}
	}, [columns]);

	function refreshData() {
		tableRef.current.onQueryChange({});
	}

	useInterval(refreshData, isRunning ? delay : null);

	const data = (query: Query<SystemLogType>) =>
		new Promise<QueryResult<SystemLogType>>((resolve, reject) => {
			const formData: any = new FormData();

			formData.append("page", query.page);
			formData.append("size", query.pageSize);

			if (query.orderBy && query.orderDirection) {
				formData.append("sort", `${query.orderBy.field},${query.orderDirection}`);
			}

			query.filters.forEach((filter: any) => {
				if (filter.column.field === "timestamp") {
					if (filter.value[0] || filter.value[1]) {
						formData.append(
							filter.column.field,
							moment(filter.value[0]).toISOString()
						);
						formData.append(
							filter.column.field,
							moment(filter.value[1]).toISOString()
						);
					}
				} else {
					formData.append(filter.column.field, filter.value);
				}
			});

			searchSystemLog(formData)
				// @ts-ignore
				.then(({ data } = {}) => {
					resolve({
						page: data.number,
						data: data.content,
						totalCount: data.totalElements
					});
				})
				.catch(() => resolve({ page: 0, totalCount: 0, data: [] }));
		});

	return (
		<Layout>
			<MaterialTable
				onChangeRowsPerPage={size => setActualPageSize(size)}
				title={t("pages.systemlog.log")}
				tableRef={tableRef}
				icons={icons}
				localization={getMaterialTableLocalization()}
				columns={columns}
				options={{
					...options,
					filtering: filtering
				}}
				actions={[
					{
						icon: () => (
							<>
								<SyncIcon />
								{isRunning && (
									<CircularProgress
										variant="indeterminate"
										className={classes.progress}
									/>
								)}
							</>
						),
						isFreeAction: true,
						tooltip: t("pages.systemlog.auto"),
						onClick: () => setIsRunning(!isRunning)
					},
					{
						icon: () => <RefreshIcon />,
						isFreeAction: true,
						tooltip: t("pages.systemlog.refresh"),
						onClick: () => refreshData()
					},
					{
						icon: () => <FilterList />,
						isFreeAction: true,
						tooltip: t("pages.systemlog.filter"),
						onClick: () => setFiltering(!filtering)
					}
				]}
				detailPanel={rowData => {
					if (rowData.message) {
						return (
							<Box p={2}>
								<Grid container direction="column">
									<Grid item>
										<Typography className={classes.textTitle}>
											{t("pages.systemlog.message")}
										</Typography>
										<Typography component="pre" className={classes.text}>
											{rowData.message}
										</Typography>
									</Grid>
									<Grid item>
										<Typography component="span" className={classes.textTitle}>
											{t("pages.systemlog.thread")}
										</Typography>
										<Typography component="span">{rowData.thread}</Typography>
									</Grid>
									<Grid item>
										<Typography component="span" className={classes.textTitle}>
											{t("pages.systemlog.logger")}
										</Typography>
										<Typography component="span">{rowData.logger}</Typography>
									</Grid>
								</Grid>
							</Box>
						);
					} else {
						return (
							<Box p={2}>
								<Typography>{t("pages.systemlog.noMessage")}</Typography>
							</Box>
						);
					}
				}}
				data={data}
				components={{
					FilterRow: filterElementProps => <TableFilter {...filterElementProps} />
				}}
			/>
		</Layout>
	);
};

export default SystemLog;
